import { useWindowSize } from '@react-hook/window-size/throttled';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import { getTheme } from '../../theme/getTheme';
import { LatestUpdates as ILatestUpdates } from '../../types/contentstack';
import { getAssetUrl } from '../../utils/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { ResponsiveContainer } from '../responsive';
import { Icon } from '../ui/icon';

export const LatestUpdates: FunctionComponent<ILatestUpdates> = ({
  title,
  updates,
}) => {
  const { breakpoints } = getTheme();
  const [windowWidth] = useWindowSize();

  const slider = useRef<Slider>(null);
  const [ticked, setTicked] = useState(false);

  let slidesToShow = 2;

  if (ticked && !!windowWidth) {
    if (windowWidth < breakpoints.md) {
      slidesToShow = 1;
    }
  }

  useEffect(() => {
    setTicked(true);
  }, []);

  const goToPrev = () => {
    slider.current?.slickPrev();
  };

  const goToNext = () => {
    slider.current?.slickNext();
  };

  if (!updates || updates.length === 0) {
    return null;
  }

  return (
    <WidthDelimiter id="updates">
      <SliderContainer>
        <ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Title style={{ display: 'flex', alignSelf: 'center' }}>
              {title}
            </Title>
          </div>
        </ResponsiveContainer>

        <CarouselChrome>
          <LeftFade />
          <RightFade />
        </CarouselChrome>

        <Slider
          ref={slider}
          autoplay={true}
          autoplaySpeed={4000}
          pauseOnHover={true}
          infinite={true}
          slidesToShow={slidesToShow}
          centerMode={true}
          centerPadding="8%"
          slidesToScroll={1}
          initialSlide={0}
          dots={false}
          arrows={false}
        >
          {updates?.map((update, idx) => {
            const { title, url, logoConnection, fileConnection } = update ?? {};

            const logo = getAssetUrl(logoConnection);
            const file = getAssetUrl(fileConnection);
            const isPdf = !!file;
            const ctaLabel = isPdf ? 'View PDF' : 'Learn more';

            return (
              <Slide key={title} className="slide">
                <Card
                  role="button"
                  tabIndex={0}
                  aria-label={title || 'resource'}
                  onClick={() => {
                    window.open(file || url || '', '_blank');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      window.open(file || url || '', '_blank');
                    }
                  }}
                >
                  {!!logo && (
                    <MaskedLogo
                      aria-label="logo"
                      $src={logo}
                      className="logo-mask"
                    />
                  )}
                  <h3 className="card-title">{title}</h3>
                  <div className="cta-row">
                    <span className="cta-text">{ctaLabel}</span>
                    <span className="cta-arrow" aria-hidden="true">
                      <Icon
                        name="external-link"
                        size={16}
                        style={{
                          position: 'relative',
                          left: '3px',
                          top: '-1px',
                        }}
                      />
                    </span>
                  </div>
                </Card>
              </Slide>
            );
          })}
        </Slider>

        <NavigationControls>
          <NavButton onClick={goToPrev} aria-label="Previous slide">
            <Icon name="arrow-left" size={20} />
          </NavButton>
          <NavButton onClick={goToNext} aria-label="Next slide">
            <Icon name="arrow-right" size={20} />
          </NavButton>
        </NavigationControls>
      </SliderContainer>
    </WidthDelimiter>
  );
};

export const LATEST_UPDATES_QUERY = /* gql */ `
  latest_updates: all_latest_updates {
    items {
      title
      updates {
        title
        url
        logoConnection { edges { node { url } } }
        fileConnection { edges { node { url } } }
      }
    }
  }
`;

const WidthDelimiter = styled(ResponsiveContainer)`
  position: relative;
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;

  .slick-dots {
    margin-top: 20px;
    margin-bottom: auto;
    display: flex !important;
    justify-content: center;
    align-items: center;

    li {
      display: flex;
      width: 12px;
      height: 12px;
      border-radius: 100%;
      background: ${({ theme }) => theme.colors.gray};
      margin: 10px;
      cursor: pointer;

      button {
        width: 100%;
        height: 100%;
        appearance: none;
        background: none;
        font-size: 1px;
        cursor: pointer;
        color: ${({ theme }) => theme.colors.gray};
      }

      &.slick-active {
        background: ${({ theme }) => theme.colors.teal};

        button {
          color: ${({ theme }) => theme.colors.teal};
        }
      }
    }
  }
`;

const Slide = styled.div`
  padding: 0 24px;
  ${mediaScreen('xs', 'md')} {
    padding: 0 12px;
  }
`;

const Card = styled.div`
  --card-bg: #262f40;
  --card-hover-gradient:
    radial-gradient(
      circle at top left,
      rgba(255, 150, 50, 0.25),
      transparent 60%
    ),
    radial-gradient(
      circle at top right,
      rgba(50, 200, 150, 0.25),
      transparent 70%
    ),
    radial-gradient(circle at bottom left, #262f40, transparent 80%), #262f40;
  --card-border: rgba(255, 255, 255, 0.06);
  --card-border-active: ${(p) => p.theme.colors.teal};
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--card-bg);
  border-radius: 18px;
  padding: 24px;
  min-height: 250px;
  cursor: pointer;
  border: 1px solid var(--card-border);
  transition:
    border-color 0.2s ease,
    transform 0.2s ease,
    background 0.2s ease;
  outline: none;
  overflow: hidden;
  z-index: 0;

  > * {
    position: relative;
    z-index: 1;
  }

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 18% 18%,
      rgba(255, 255, 255, 0.07),
      transparent 65%
    );
    opacity: 0.35;
    mix-blend-mode: overlay;
    pointer-events: none;
    z-index: 0;
  }
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--card-hover-gradient);
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
    z-index: 0;
  }
  &:hover,
  &:focus-visible {
    border-color: var(--card-border-active);
  }
  &:hover:after {
    opacity: 1;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px var(--card-border-active);
  }

  .logo-mask {
    height: 34px;
    width: auto;
  }
  h3.card-title {
    font-size: 20px;
    line-height: 30px;
    margin: 24px 0 0;
    flex: 1;
    font-weight: 600;
    color: #fff;
  }
  .cta-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 18px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.teal};
  }

  ${mediaScreen('xs', 'md')} {
    padding: 26px 26px 24px;
    min-height: 240px;
    h3.card-title {
      font-size: 18px;
      line-height: 26px;
      margin-top: 40px;
    }
    .cta-row {
      margin-top: 26px;
    }
  }
  ${mediaScreen('sm', 'lg')} {
    h3.card-title {
      max-width: 480px;
    }
  }
  &:hover .logo-mask {
    filter: none;
    background: #fff;
  }
`;

const Title = styled.h2`
  font-size: 50px;
  line-height: 1.2;
  color: ${(props) => props.theme.colors.white};
  font-weight: 700;
  margin: 0 0 50px;
  text-align: center;

  ${mediaScreen('xs', 'md')} {
    font-size: 32px;
    margin-bottom: 35px;
  }
`;

const CarouselChrome = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
`;

const LeftFade = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 13%;
  height: 100%;
  background: linear-gradient(90deg, #181e2b 0%, rgba(24, 30, 43, 0) 100%);
`;

const RightFade = styled(LeftFade)`
  left: auto;
  right: 0;
  transform: scaleX(-1);
`;

const NavigationControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 60px;
  z-index: 10;
  position: relative;

  ${mediaScreen('xs', 'md')} {
    margin-top: 24px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: ${(p) => p.theme.colors.white};
  cursor: pointer;

  &:hover {
    border-color: ${(p) => p.theme.colors.teal};
    color: ${(p) => p.theme.colors.teal};
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.colors.teal};
  }
`;

const MaskedLogo = styled.div<{ $src: string }>`
  background: #5d6e8e;
  -webkit-mask-image: url(${(p) => p.$src});
  mask-image: url(${(p) => p.$src});
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: left center;
  mask-position: left center;
  -webkit-mask-size: contain;
  mask-size: contain;
  height: 34px;
  transition:
    background 0.2s ease,
    filter 0.2s ease;
`;
